import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import Divider from '@adsk/alloy-react-divider';
import {
  ControlLabel,
  Placeholder,
  useDropdown,
  UseDropdownProps,
} from '@adsk/alloy-react-dropdown';
import {
  Container,
  optionalPropType,
  useDeprecate,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

import { PERMISSION_MENU_ITEM_SIZES } from './consts';
import { permissionShape } from './helpers/propTypes';
import { isPermissionGroup } from './helpers/typeGuards';
import PermissionIndicator from './layout/PermissionIndicator';
import PermissionMenu from './layout/PermissionMenu';
import PermissionValue from './layout/PermissionValue';
import {
  GroupHeader,
  PermissionGroup,
  ItemSize,
  Permission,
  Option,
} from './types';

const getControlStyle = ({
  showBorder,
  disabled,
}: {
  showBorder: boolean;
  disabled?: boolean;
}): CSSProp => ({
  height: '40px',
  paddingLeft: theme.spacing.M,
  boxShadow: 'none',
  ...(!showBorder && {
    border: `2px solid transparent`,
  }),
  ...(disabled && {
    backgroundColor: theme.tokens.colors.background.primary.value,
  }),
  '&:hover': {
    backgroundColor: theme.tokens.colors.background.neutral.hover.value,
    '> button': {
      visibility: 'visible',
    },
  },
  '&:active': {
    backgroundColor: theme.tokens.colors.background.neutral.pressed.value,
    ...(!showBorder && {
      border: `2px solid transparent`,
    }),
  },
  '&:hover, &:active': {
    boxShadow: 'none',
    '.PermissionValue__pill--gray': {
      backgroundColor: theme.tokens.colors.background.neutral.inactive.value,
    },
  },
  '&:focus-within': !showBorder
    ? {
        boxShadow: 'none',
        border: `2px solid ${theme.colors.focusHalo}`,
      }
    : {
        boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
      },
  '> button': {
    visibility: 'hidden',
  },
});

const getControlOpenStyle = ({
  showBorder,
}: {
  showBorder: boolean;
}): CSSProp => ({
  '&, &:hover, &:active, &:focus-within': {
    ...(!showBorder
      ? { border: `2px solid ${theme.colors.focusHalo}` }
      : {
          boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
        }),
    backgroundColor: theme.tokens.colors.background.primary.value,
    '.PermissionValue__pill--gray': {
      backgroundColor: theme.tokens.colors.background.neutral.inactive.value,
    },
  },
  '> button': {
    visibility: 'visible',
  },
});

type PermissionSelectProps = Omit<
  UseDropdownProps<Permission | PermissionGroup>,
  'defaultValue' | 'placeholder' | 'items' | 'groupedItems'
> & {
  className?: string;
  style?: CSSProp | null;
  pillCount?: number;
  showGroupLabel?: boolean;
  permissions: (Permission | PermissionGroup)[];
  defaultIsOpen?: boolean;
  itemSize?: ItemSize;
  showBorder?: boolean;
  disabled?: boolean;
  overlayClassName?: string;
  overlayStyle?: CSSProp;
  defaultValue?: { value?: string | number };
  maxMenuHeight?: number;
  container?: Container;
  /** @deprecated use error field instead */
  invalid?: boolean;
  error?: boolean;
  placeholder?: ReactNode;
  renderFooter?: () => ReactNode;
};

/**
 * PermissionSelect
 * Implement permission select dropdown based on SelectBox to let user show customized permission level.
 */
const PermissionSelect = React.forwardRef<
  HTMLLabelElement,
  PermissionSelectProps
>(
  (
    {
      className,
      style,
      pillCount = 4,
      showGroupLabel = false,
      permissions,
      defaultIsOpen = false,
      itemSize = PERMISSION_MENU_ITEM_SIZES.NORMAL,
      showBorder = false,
      disabled,
      overlayClassName,
      overlayStyle,
      maxMenuHeight,
      defaultValue,
      container,
      invalid = false,
      error,
      placeholder,
      renderFooter,
      ...rest
    },
    ref,
  ) => {
    useDeprecate(invalid, 'PermissionSelect', 'invalid', 'error');

    error = error !== undefined ? error : invalid;

    const items = React.useMemo(
      () =>
        permissions.reduce<(GroupHeader | Option)[]>((parsedList, current) => {
          if (isPermissionGroup(current)) {
            parsedList.push({
              ...current,
              isGroupTitle: true,
              disabled: true,
            });

            current.options.forEach((item) => {
              parsedList.push({
                ...item,
                groupLabel: current.label,
                pillValue: item.pillValue || current.pillValue,
              });
            });
          } else {
            parsedList.push(current);
          }
          return parsedList;
        }, []),
      [permissions],
    );

    const target = useForwardedRef(ref);

    const defaultSelectedItem = items.find(
      (option) => option.value === defaultValue?.value,
    );

    const { controlProps, menuProps, openMenu, closeMenu } = useDropdown({
      items,
      defaultSelectedItem,
      defaultIsOpen,
      target,
      ...rest,
    });

    const renderMenu = () => (
      <PermissionMenu
        pillCount={pillCount}
        itemSize={itemSize}
        {...menuProps}
        style={[{ maxHeight: maxMenuHeight }, overlayStyle]}
        className={overlayClassName}
        renderFooter={
          renderFooter
            ? () => (
                <>
                  <Divider />
                  {renderFooter()}
                </>
              )
            : undefined
        }
      />
    );

    const { isOpen, selectedItem } = controlProps;

    return (
      <>
        <ControlLabel
          className={className}
          style={[
            getControlStyle({ showBorder, disabled }),
            isOpen && getControlOpenStyle({ showBorder }),
            style,
          ]}
          disabled={disabled}
          error={error}
          {...controlProps}
        >
          {!selectedItem && placeholder ? (
            <Placeholder>{placeholder}</Placeholder>
          ) : (
            <PermissionValue
              permission={selectedItem}
              permissions={items}
              pillCount={pillCount}
              showGroupLabel={showGroupLabel}
              onClick={(e) => {
                if (e.currentTarget === e.target) {
                  // Prevent openMenu from firing when the toggle button is clicked while menu is open
                  isOpen ? closeMenu() : openMenu();
                }
              }}
              disabled={disabled}
            />
          )}
        </ControlLabel>
        {isOpen ? (
          <Portal container={container}>{renderMenu()}</Portal>
        ) : (
          renderMenu()
        )}
      </>
    );
  },
);

PermissionSelect.displayName = 'PermissionSelect';

PermissionSelect.propTypes = {
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * style for the component
   */
  style: stylePropType,
  /**
   * Default selected value for the component
   */
  defaultValue: PropTypes.shape({
    value: optionalPropType(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
  }),
  /**
   * When display group label (if have) at the value renderer instead of the selected option label
   */
  showGroupLabel: PropTypes.bool,
  /**
   * Show the default SelectBox border style
   */
  showBorder: PropTypes.bool,
  /**
   * The pill count
   */
  pillCount: PropTypes.number,
  /**
   * Array of permissions
   */
  permissions: PropTypes.arrayOf(permissionShape.isRequired).isRequired,
  /**
   * Whether the menu is open by default
   */
  defaultIsOpen: PropTypes.bool,
  /**
   * Whether the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The max height of the menu, the footer is not included
   */
  maxMenuHeight: PropTypes.number,
  /**
   * The menu item size
   */
  itemSize: PropTypes.oneOf(Object.values(PermissionMenu.ITEM_SIZES)),
  /**
   * The style object to applied to the overlay (menu)
   */
  overlayStyle: stylePropType,
  container: PropTypes.any,
  /** **DEPRECATED** use error field instead.  Toggle error state */
  invalid: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  placeholder: PropTypes.any,
  renderFooter: PropTypes.func,
};

export default Object.assign(PermissionSelect, {
  PILL_TYPES: PermissionIndicator.PILL_TYPES,
  ITEM_SIZES: PermissionMenu.ITEM_SIZES,
  Indicator: PermissionIndicator,
});
